import type {
  ComposeOutput,
  ComposeSource,
  ComposeUpload,
  FactiiiContext
} from './composeUpload.types';
import { getFactiiiColor } from './getFactiiiColor';
import { getS3ObjectURL } from './s3';

// Logo SVG path from `shared/utils/icons/Logo.tsx` — kept in sync so the
// canvas-rendered watermark glyph matches the rendered <Logo /> component
// used in <Avatar />. Both render indigo bg + white F. The 2px border in
// `getFactiiiColor(factiiiId)` mirrors Avatar's per-factiii colored border.
const LOGO_PATH =
  'M56.22 31.73C51.46 31.73 47.8617 32.7783 45.425 34.875C42.9883 36.915 41.77 40.0033 41.77 44.14V47.795H66.25V58.845H41.77V80.52H28V44.225C28 36.575 30.38 30.625 35.14 26.375C39.9567 22.125 46.6717 20 55.285 20C58.685 20 61.8867 20.3683 64.89 21.105C67.8933 21.785 70.5 22.805 72.71 24.165L68.46 35.045C65.06 32.835 60.98 31.73 56.22 31.73Z';
const LOGO_BG = '#6366F1';

/**
 * Web compose helper. Reads source bytes, hashes them (raw sha256), then
 * paints a watermarked JPEG view variant via canvas. HEIC sources are decoded
 * to a working bitmap via heic2any (lazy-loaded) but rawBlob preserves the
 * original HEIC bytes — server stores RAW unmodified.
 *
 * Sibling: composeUpload.native.ts (mobile, expo-image-manipulator).
 */

const HEIC_MIMES = new Set(['image/heic', 'image/heif']);

const sha256Hex = async (blob: Blob): Promise<string> => {
  const buf = await blob.arrayBuffer();
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
};

const blobToImage = async (blob: Blob): Promise<HTMLImageElement> => {
  const url = URL.createObjectURL(blob);
  try {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = () => reject(new Error('Failed to decode image'));
      img.src = url;
    });
    return img;
  } finally {
    URL.revokeObjectURL(url);
  }
};

// Watermark composite — mirrors how <Avatar factiiiAvatarKey={...} factiiiId={...} />
// renders in the rest of the app: avatar image when present, indigo Logo
// (white F path) fallback otherwise, with a 2px factiii-colored border.
const drawAvatarOverlay = (
  ctx: CanvasRenderingContext2D,
  cw: number,
  ch: number,
  avatar: HTMLImageElement | null,
  factiiiId: number
) => {
  const size = Math.max(40, Math.min(cw, ch) * 0.06);
  const pad = size * 0.4;
  const x = cw - size - pad;
  const y = ch - size - pad;
  const r = size * 0.2;

  // Rounded-rect path — used for both the inner clip (avatar/Logo bg) and
  // the outer stroke (factiii-color border).
  const traceRoundedRect = () => {
    ctx.beginPath();
    ctx.moveTo(x + r, y);
    ctx.lineTo(x + size - r, y);
    ctx.quadraticCurveTo(x + size, y, x + size, y + r);
    ctx.lineTo(x + size, y + size - r);
    ctx.quadraticCurveTo(x + size, y + size, x + size - r, y + size);
    ctx.lineTo(x + r, y + size);
    ctx.quadraticCurveTo(x, y + size, x, y + size - r);
    ctx.lineTo(x, y + r);
    ctx.quadraticCurveTo(x, y, x + r, y);
    ctx.closePath();
  };

  ctx.save();
  ctx.globalAlpha = 0.85;
  traceRoundedRect();
  ctx.clip();

  if (avatar) {
    ctx.drawImage(avatar, x, y, size, size);
  } else {
    // Indigo Rect + white Logo F path scaled from the 100×100 viewBox.
    ctx.fillStyle = LOGO_BG;
    ctx.fillRect(x, y, size, size);
    ctx.save();
    ctx.translate(x, y);
    ctx.scale(size / 100, size / 100);
    ctx.fillStyle = 'white';
    ctx.fill(new Path2D(LOGO_PATH));
    ctx.restore();
  }
  ctx.restore();

  // 2px border in the factiii-derived color (matches Avatar.tsx's border).
  ctx.save();
  ctx.globalAlpha = 0.85;
  traceRoundedRect();
  ctx.lineWidth = 2;
  ctx.strokeStyle = getFactiiiColor(factiiiId);
  ctx.stroke();
  ctx.restore();
};

const decodeHeicIfNeeded = async (blob: Blob): Promise<Blob> => {
  if (!HEIC_MIMES.has(blob.type)) return blob;
  // Lazy-load heic2any only when actually needed (~1MB bundle hit)
  const mod = await import('heic2any');
  const heic2any = mod.default;
  const result = await heic2any({ blob, toType: 'image/jpeg', quality: 0.95 });
  return Array.isArray(result) ? result[0] : result;
};

const sourceToBlob = async (s: ComposeSource): Promise<Blob> => {
  if (s.kind === 'blob') return s.blob;
  return (await fetch(s.uri)).blob();
};

export const composeUpload: ComposeUpload = async (
  source: ComposeSource,
  factiii: FactiiiContext | null
): Promise<ComposeOutput> => {
  const rawBlob = await sourceToBlob(source);
  const sha256 = await sha256Hex(rawBlob);
  const contentType = rawBlob.type || 'application/octet-stream';

  // Non-image uploads: skip view variant — caller stores RAW only.
  if (!contentType.startsWith('image/')) {
    return {
      rawBlob,
      viewBlob: rawBlob,
      rawSize: rawBlob.size,
      viewSize: rawBlob.size,
      sha256,
      viewSha256: sha256,
      contentType,
      viewWidth: 0,
      viewHeight: 0
    };
  }

  // Decode (HEIC if needed) into a working bitmap.
  const decodable = await decodeHeicIfNeeded(rawBlob);
  const img = await blobToImage(decodable);

  const canvas = document.createElement('canvas');
  canvas.width = img.naturalWidth;
  canvas.height = img.naturalHeight;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get 2d canvas context');
  ctx.drawImage(img, 0, 0);

  if (factiii) {
    const avatarImg = factiii.factiiiAvatarKey
      ? await blobToImage(
          await (await fetch(getS3ObjectURL(factiii.factiiiAvatarKey))).blob()
        )
      : null;
    drawAvatarOverlay(
      ctx,
      canvas.width,
      canvas.height,
      avatarImg,
      factiii.factiiiId
    );
  }

  const viewBlob = await new Promise<Blob>((resolve, reject) =>
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('toBlob failed'))),
      'image/jpeg',
      0.8
    )
  );
  const viewSha256 = await sha256Hex(viewBlob);

  return {
    rawBlob,
    viewBlob,
    rawSize: rawBlob.size,
    viewSize: viewBlob.size,
    sha256,
    viewSha256,
    contentType,
    viewWidth: canvas.width,
    viewHeight: canvas.height
  };
};
